import { useToast } from '@/hooks/use-toast';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useAuthStore } from '@/state/store/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { invalidateLeaveRequestQueries } from '../../leave-request-queries';
import {
  getLeaveCategoriesLookup,
  getLeaveRequests,
  getMyCurrentLeaveAllotments,
  previewLeaveRequest,
  submitLeaveRequest,
  uploadLeaveDocuments,
  withdrawLeaveRequest,
} from '../services/leave.service';
import {
  GetLeaveRequestsParams,
  LeaveCategoriesLookupResult,
  LeaveRequestSubmitWorkflowInput,
  LeaveWorkflowResult,
} from '../types/leave.types';
import { leaveTypeOptionsOf } from '../utils/leave-type-options';

export const useCurrentUserId = () => {
  const user = useAuthStore((state) => state.user);
  return user?.itemId ?? '';
};

export const useGetLeaveRequests = (params: GetLeaveRequestsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-leave-requests', params],
    queryFn: () => getLeaveRequests(params),
    staleTime: 60 * 1000,
  });
};

export const useGetLeaveCategoriesLookup = () => {
  return useGlobalQuery({
    queryKey: ['hr-leave-categories-lookup'],
    queryFn: getLeaveCategoriesLookup,
    staleTime: 30 * 60 * 1000,
  });
};

/** The leave types the signed-in employee may request (their current HR term's applicable ones). */
export const useGetMyLeaveTypes = () => {
  const userId = useCurrentUserId();
  const { data: categoriesData, isLoading: isCategoriesLoading } = useGetLeaveCategoriesLookup();
  const { data: term, isLoading: isTermLoading } = useGlobalQuery({
    queryKey: ['hr-my-leave-types', userId],
    queryFn: () => getMyCurrentLeaveAllotments(userId),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });
  const categories = (categoriesData as { LeaveCategorys?: LeaveCategoriesLookupResult })
    ?.LeaveCategorys?.items;
  const options = useMemo(
    () => leaveTypeOptionsOf(term?.allotments ?? [], categories ?? [], term?.employmentType),
    [term, categories]
  );
  return { options, isLoading: isCategoriesLoading || isTermLoading };
};

/** The workflow's preview of a request (days, hours, overtime left), once a category and both days are
 * picked. A plain query: a failed preview shows nothing rather than an error toast. */
export const useLeaveRequestPreview = (
  input: Omit<LeaveRequestSubmitWorkflowInput, 'preview' | 'reason'> | null
) => {
  return useQuery({
    queryKey: ['hr-leave-request-preview', input],
    queryFn: () => previewLeaveRequest(input!),
    enabled: Boolean(input),
    staleTime: 60 * 1000,
    retry: false,
  });
};

/** Toasts the workflow's own answer: its refusal reason (e.g. a notice-period or HR-period rule) as a
 * destructive toast, or `successKey` when it went through. */
const useLeaveWorkflowToast = () => {
  const { toast } = useToast();
  const { t } = useTranslation();

  return (result: LeaveWorkflowResult, successKey: string) => {
    if (result.IsSuccessful) {
      toast({ title: result.Message || t(successKey) });
    } else {
      toast({ title: result.Message || t('SOMETHING_WENT_WRONG'), variant: 'destructive' });
    }
  };
};

/** Submits a leave request; `files` (a sick leave's doctor's documents) are uploaded first, and a failed
 * upload answers like a refusal, so nothing is submitted then. */
export const useSubmitLeaveRequest = () => {
  const queryClient = useQueryClient();
  const toastResult = useLeaveWorkflowToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: async ({
      files = [],
      ...input
    }: LeaveRequestSubmitWorkflowInput & { files?: File[] }): Promise<LeaveWorkflowResult> => {
      let fileIds: string[] = [];
      try {
        if (files.length) fileIds = await uploadLeaveDocuments(files);
      } catch {
        return { IsSuccessful: false, Message: t('LEAVE_DOCUMENT_UPLOAD_FAILED') };
      }
      return submitLeaveRequest(fileIds.length ? { ...input, fileIds } : input);
    },
    onSuccess: (result) => {
      invalidateLeaveRequestQueries(queryClient);
      toastResult(result, 'LEAVE_REQUEST_SUBMITTED');
    },
  });
};

export const useWithdrawLeaveRequest = () => {
  const queryClient = useQueryClient();
  const toastResult = useLeaveWorkflowToast();

  return useGlobalMutation({
    mutationFn: (itemId: string) => withdrawLeaveRequest(itemId),
    onSuccess: (result) => {
      invalidateLeaveRequestQueries(queryClient);
      toastResult(result, 'LEAVE_REQUEST_WITHDRAWN');
    },
  });
};
